// Backlog 071-B — typed runtime-error rendering for `grid plug` failures.
//
// When wait_rollout fails because a pod hit CrashLoopBackOff, ImagePullBackOff,
// or OOMKilled, the deployer attaches a structured `details` payload to the
// failed step entry on the deploy trace. This module turns that payload into
// a human-readable tail mirroring PR #269's build-log box, so the developer
// sees the actionable detail (stderr, registry message, memory hint) inline
// instead of having to round-trip to `kubectl logs --previous`.
//
// The corresponding API code is in `packages/api/src/services/deployer.ts`
// (`mapRolloutFailureToError`) and `kubernetes.ts` (`RolloutResult`).

import type { DeployTrace } from './plug-poll.js';
import type { Step } from '../render/multi-step.js';

/** Error codes the deployer emits for typed runtime failures. */
export const RUNTIME_ERROR_CODES = new Set([
  'RUNTIME_CRASHLOOP',
  'IMAGE_PULL_ERROR',
  'OOM_KILLED',
]);

interface CrashloopDetails {
  service: string;
  pod: string;
  stderr_tail: string;
  restart_count: number;
}

interface ImagePullDetails {
  service: string;
  pod: string;
  message: string;
}

interface OomDetails {
  service: string;
  pod: string;
}

/**
 * Pull the failed wait_rollout step out of the trace, if present. Returns
 * `null` when wait_rollout didn't fail or when the typed payload is missing
 * (e.g. older API versions that don't yet attach `details`).
 */
export function findFailedRolloutStep(trace: DeployTrace): Step | null {
  const step = trace.steps.find(
    (s) => s.status === 'failed' && s.step === 'wait_rollout',
  );
  return step ?? null;
}

/**
 * Render the boxed stderr tail for a CrashLoopBackOff pod. Matches the visual
 * shape of the build-log tail (PR #269) so the failure surface stays
 * consistent across build-time and runtime errors. Includes the kubectl
 * fallback line on the bottom edge so a developer who needs the *full* log
 * has the exact command at hand.
 *
 * `namespace` is the K8s namespace; surfaced only on the kubectl line so the
 * (ugly) `<orgid8>-<entityid8>` slug doesn't bleed into user-facing copy.
 */
export function renderCrashloopTail(opts: {
  details: CrashloopDetails;
  namespace: string;
}): string {
  const { details, namespace } = opts;
  // Belt-and-suspenders: server-side resolves entity.namespace via
  // entityNamespace() so this should always be a non-empty string. If a
  // stale/older API ever sends through an empty value, drop the `-n <ns>`
  // portion so the user never sees `kubectl logs ... -n null`.
  const nsArg = namespace ? ` -n ${namespace}` : '';
  const lines = details.stderr_tail.split(/\r?\n/).filter((l) => l.length > 0);
  const out: string[] = [];
  if (lines.length === 0) {
    out.push(
      `  Pod ${details.pod} crashing (${details.restart_count} restart${details.restart_count === 1 ? '' : 's'}); previous-container logs unavailable.`,
    );
    out.push(
      `  full pod logs: kubectl logs ${details.pod} --previous${nsArg}`,
    );
    return out.join('\n');
  }
  const restarts = `${details.restart_count} restart${details.restart_count === 1 ? '' : 's'}`;
  out.push(
    `  ┌─ last ${lines.length} line${lines.length === 1 ? '' : 's'} of pod logs (${details.pod}, ${restarts}) ─`,
  );
  for (const line of lines) {
    out.push(`  │ ${line}`);
  }
  out.push(
    `  └─ full pod logs: kubectl logs ${details.pod} --previous${nsArg}`,
  );
  return out.join('\n');
}

/** Render the inline detail line for an ImagePullBackOff / ErrImagePull pod. */
export function renderImagePullTail(opts: {
  details: ImagePullDetails;
}): string {
  const out: string[] = [];
  out.push(`  Image pull failed for ${opts.details.service}.`);
  if (opts.details.message) {
    out.push(`  ${opts.details.message}`);
  }
  return out.join('\n');
}

/** Render the inline detail line for an OOMKilled pod. */
export function renderOomTail(opts: { details: OomDetails }): string {
  return [
    `  Pod ${opts.details.pod} OOM-killed during startup.`,
    `  Increase the memory limit for ${opts.details.service} in cloudgrid.yaml and re-plug.`,
  ].join('\n');
}

/**
 * Top-level renderer: read the failed wait_rollout step and dispatch to the
 * right per-variant tail renderer. Returns `null` when there's nothing useful
 * to surface — caller falls back to the standard error message.
 *
 * Intentionally takes the trace + namespace separately rather than reading
 * namespace from `trace.entity` so callers can inject a stub for tests.
 */
export function runtimeErrorTailFor(
  trace: DeployTrace,
): string | null {
  const step = findFailedRolloutStep(trace);
  if (!step) return null;
  const code = step.code;
  if (typeof code !== 'string' || !RUNTIME_ERROR_CODES.has(code)) return null;

  const details = step.details as Record<string, unknown> | undefined;
  if (!details || typeof details !== 'object') return null;

  const namespace = trace.entity.namespace;

  if (code === 'RUNTIME_CRASHLOOP') {
    if (
      typeof details.service !== 'string' ||
      typeof details.pod !== 'string' ||
      typeof details.stderr_tail !== 'string' ||
      typeof details.restart_count !== 'number'
    ) {
      return null;
    }
    return renderCrashloopTail({
      details: details as unknown as CrashloopDetails,
      namespace,
    });
  }

  if (code === 'IMAGE_PULL_ERROR') {
    if (
      typeof details.service !== 'string' ||
      typeof details.pod !== 'string' ||
      typeof details.message !== 'string'
    ) {
      return null;
    }
    return renderImagePullTail({ details: details as unknown as ImagePullDetails });
  }

  if (code === 'OOM_KILLED') {
    if (
      typeof details.service !== 'string' ||
      typeof details.pod !== 'string'
    ) {
      return null;
    }
    return renderOomTail({ details: details as unknown as OomDetails });
  }

  return null;
}
